import React, { useState, useEffect, useContext } from 'react';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { UserCheck, UserX, ChevronDown, ChevronRight, Clock, Users } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';

// Day colors matching roster page
const dayColors = {
  Sunday: { bg: 'bg-purple-600', light: 'bg-purple-50', border: 'border-purple-200' },
  Monday: { bg: 'bg-blue-600', light: 'bg-blue-50', border: 'border-blue-200' },
  Tuesday: { bg: 'bg-teal-600', light: 'bg-teal-50', border: 'border-teal-200' },
  Wednesday: { bg: 'bg-amber-600', light: 'bg-amber-50', border: 'border-amber-200' },
  Thursday: { bg: 'bg-red-600', light: 'bg-red-50', border: 'border-red-200' }
};

const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'];

const Attendance = () => {
  const { user } = useContext(AuthContext);
  const [rosters, setRosters] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [stocktakes, setStocktakes] = useState([]);
  const [selectedStocktake, setSelectedStocktake] = useState('');
  const [openDays, setOpenDays] = useState(['Sunday']); // Sunday open by default
  const token = localStorage.getItem('token');

  // Get the current store_id (for Network Admin, check localStorage)
  const getStoreId = () => {
    if (user?.role === 'network_admin' || user?.role === 'regional_manager') {
      return localStorage.getItem('selectedStoreId') || user?.store_id;
    }
    return user?.store_id;
  };

  useEffect(() => {
    fetchData();
  }, [user?.store_id]);

  const fetchData = async () => {
    try {
      const storeId = getStoreId();
      const storeParam = storeId ? `?store_id=${storeId}` : '';
      
      const [rostersRes, attendanceRes, stocktakesRes] = await Promise.all([
        fetch(`${API}/rosters${storeParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/attendance${storeParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/stocktakes${storeParam}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setRosters(await rostersRes.json());
      setAttendance(await attendanceRes.json());
      setStocktakes(await stocktakesRes.json());
    } catch (error) {
      console.error(error);
    }
  };

  const markAttendance = async (rosterId, status) => {
    try {
      // Find the roster to get user info
      const roster = rosters.find(r => r.id === rosterId);
      const storeId = getStoreId();
      
      await fetch(`${API}/attendance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          roster_id: rosterId, 
          user_id: roster?.user_id || rosterId,
          store_id: storeId,
          status 
        })
      });
      toast.success(`Marked as ${status === 'present' ? 'Present' : 'No Show'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to mark attendance');
    }
  };

  const getAttendanceStatus = (rosterId) => {
    const record = attendance.find(a => a.roster_id === rosterId);
    return record?.status || null;
  };

  const filteredRosters = selectedStocktake
    ? rosters.filter(r => r.stocktake_id === selectedStocktake)
    : rosters;

  // Group rosters by day
  const getRostersByDay = (day) => {
    return filteredRosters.filter(r => r.day === day);
  };

  // Get attendance stats for a day
  const getDayStats = (day) => {
    const dayRosters = getRostersByDay(day);
    const present = dayRosters.filter(r => getAttendanceStatus(r.id) === 'present').length;
    const noShow = dayRosters.filter(r => getAttendanceStatus(r.id) === 'no_show').length;
    const unmarked = dayRosters.length - present - noShow;
    return { total: dayRosters.length, present, noShow, unmarked };
  };

  const toggleDay = (day) => {
    setOpenDays(prev => 
      prev.includes(day) 
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  const markAllPresent = async (day) => {
    const dayRosters = getRostersByDay(day);
    for (const roster of dayRosters) {
      if (getAttendanceStatus(roster.id) !== 'present') {
        await markAttendance(roster.id, 'present');
      }
    }
  };

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight mb-2">Attendance</h1>
          <p className="text-slate-600">Track team member attendance by day</p>
        </div>
      </div>

      {/* Filter by Stocktake */}
      <div className="flex items-center gap-4">
        <Label className="text-sm font-semibold text-slate-700">Filter by Stocktake:</Label>
        <Select value={selectedStocktake || "all"} onValueChange={(val) => setSelectedStocktake(val === "all" ? "" : val)}>
          <SelectTrigger className="w-64" data-testid="stocktake-filter">
            <SelectValue placeholder="All stocktakes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stocktakes</SelectItem>
            {stocktakes.map((st) => (
              <SelectItem key={st.id} value={st.id}>
                {st.store_name} - {new Date(st.scheduled_date).toLocaleDateString()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Day Accordions */}
      <div className="space-y-4">
        {days.map((day) => {
          const dayRosters = getRostersByDay(day);
          const stats = getDayStats(day);
          const colors = dayColors[day];
          const isOpen = openDays.includes(day);

          return (
            <Collapsible
              key={day}
              open={isOpen}
              onOpenChange={() => toggleDay(day)}
              className="bg-white rounded-xl border border-slate-200 overflow-hidden"
              data-testid={`day-accordion-${day.toLowerCase()}`}
            >
              <CollapsibleTrigger className="w-full" data-testid={`day-trigger-${day.toLowerCase()}`}>
                <div className={`flex items-center justify-between p-4 hover:bg-slate-50 transition-colors`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-3 h-12 rounded-full ${colors.bg}`} />
                    <div className="text-left">
                      <h3 className="text-lg font-bold text-slate-900">{day}</h3>
                      <p className="text-sm text-slate-500">
                        {stats.total} team member{stats.total !== 1 ? 's' : ''} rostered
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Stats badges */}
                    {stats.total > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="flex items-center gap-1 text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                          <UserCheck className="w-3 h-3" />
                          {stats.present}
                        </span>
                        <span className="flex items-center gap-1 text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full font-medium">
                          <UserX className="w-3 h-3" />
                          {stats.noShow}
                        </span>
                        {stats.unmarked > 0 && (
                          <span className="flex items-center gap-1 text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded-full font-medium">
                            {stats.unmarked} unmarked
                          </span>
                        )}
                      </div>
                    )}
                    {isOpen ? (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <div className={`border-t border-slate-200 ${colors.light}`}>
                  {dayRosters.length === 0 ? (
                    <div className="p-8 text-center">
                      <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No team members rostered for {day}</p>
                    </div>
                  ) : (
                    <>
                      {/* Quick Actions */}
                      <div className="p-4 border-b border-slate-200 bg-white flex items-center justify-between">
                        <span className="text-sm text-slate-600">
                          Quick actions for {day}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            markAllPresent(day);
                          }}
                          className="text-green-600 border-green-200 hover:bg-green-50"
                          data-testid={`mark-all-present-${day.toLowerCase()}`}
                        >
                          <UserCheck className="w-4 h-4 mr-1" />
                          Mark All Present
                        </Button>
                      </div>

                      {/* Team Members List */}
                      <div className="divide-y divide-slate-200">
                        {dayRosters.map((roster) => {
                          const status = getAttendanceStatus(roster.id);
                          const isPresent = status === 'present';
                          const isNoShow = status === 'no_show';

                          return (
                            <div
                              key={roster.id}
                              className="p-4 bg-white flex items-center justify-between hover:bg-slate-50 transition-colors"
                              data-testid={`attendance-row-${roster.id}`}
                            >
                              <div className="flex items-center gap-4">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                                  isPresent ? 'bg-green-600' : isNoShow ? 'bg-red-500' : 'bg-slate-400'
                                }`}>
                                  {roster.user_name?.charAt(0) || '?'}
                                </div>
                                <div>
                                  <h4 className="font-semibold text-slate-900">{roster.user_name}</h4>
                                  <div className="flex items-center gap-3 text-sm text-slate-500">
                                    {roster.team_member_number && (
                                      <span className="font-mono">#{roster.team_member_number}</span>
                                    )}
                                    <span className="flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      {roster.start_time || '16:30'} - {roster.end_time || '23:00'}
                                    </span>
                                    {roster.home_store && (
                                      <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">
                                        {roster.home_store}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                {/* Status Badge */}
                                {status && (
                                  <span className={`text-xs px-2.5 py-1 rounded-full font-semibold mr-2 ${
                                    isPresent 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-red-100 text-red-700'
                                  }`}>
                                    {isPresent ? 'Present' : 'No Show'}
                                  </span>
                                )}

                                {/* Action Buttons */}
                                <Button
                                  size="sm"
                                  className={`${
                                    isPresent
                                      ? 'bg-green-600 hover:bg-green-700 text-white'
                                      : 'bg-white border border-green-300 text-green-600 hover:bg-green-600 hover:text-white'
                                  }`}
                                  onClick={() => markAttendance(roster.id, 'present')}
                                  data-testid={`mark-present-${roster.id}`}
                                >
                                  <UserCheck className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  className={`${
                                    isNoShow
                                      ? 'bg-red-500 hover:bg-red-600 text-white'
                                      : 'bg-white border border-red-300 text-red-500 hover:bg-red-500 hover:text-white'
                                  }`}
                                  onClick={() => markAttendance(roster.id, 'no_show')}
                                  data-testid={`mark-no-show-${roster.id}`}
                                >
                                  <UserX className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </div>

      {/* Summary Card */}
      {filteredRosters.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Attendance Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-3xl font-bold text-slate-900">{filteredRosters.length}</p>
              <p className="text-sm text-slate-500">Total Rostered</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">
                {filteredRosters.filter(r => getAttendanceStatus(r.id) === 'present').length}
              </p>
              <p className="text-sm text-slate-500">Present</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-3xl font-bold text-red-500">
                {filteredRosters.filter(r => getAttendanceStatus(r.id) === 'no_show').length}
              </p>
              <p className="text-sm text-slate-500">No Show</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-lg">
              <p className="text-3xl font-bold text-amber-600">
                {filteredRosters.filter(r => !getAttendanceStatus(r.id)).length}
              </p>
              <p className="text-sm text-slate-500">Unmarked</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendance;
